import { AssetType } from '../../../_shared/enums/asset-type.enum';
import { PaginatedSearchRequest } from '../../../../_shared/models/paginated-search-request.model';
import { ComplianceResult } from '../../_shared/enums/compliance-result.enum';
import { ChecklistTypeIdEnum } from '../../_shared/models/checklist-type.model';
import { ReviewError, ReviewItemBase } from '../../../_shared/models/excel-review.model';

export interface ComplianceStandard {
  id: number;
  name: string;
}

export interface Compliance {
  id: number;
  complianceName: string;
  region: string;
  complianceType: string;
  checklistSubType: string;
  checklistType: string;
  assignedAssets: number;
  /** Display labels; the raw names above stay untouched for routing and payloads. */
  complianceTypeLabel?: string;
  checklistSubTypeLabel?: string;
  checklistTypeLabel?: string;
}

import { TableColumn } from '../../compliance-overview/models/compliance-item.model';
import { translate } from '@jsverse/transloco';

/** Call from a component (not at module load) so the headers pick up the active language. */
export function getComplianceLibraryColumns(): TableColumn[] {
  return [
    { header: translate('compliance.library.columns.complianceName'), value: 'complianceName', width: 'col-2', type: 'text', truncate: true, title: true, bold: true },
    { header: translate('compliance.library.columns.region'), value: 'region', width: 'col', type: 'text', truncate: true, title: true },
    { header: translate('compliance.library.columns.complianceType'), value: 'complianceTypeLabel', width: 'col', type: 'stacked', secondaryValue: 'checklistSubTypeLabel' },
    { header: translate('compliance.library.columns.checklistTypes'), value: 'checklistTypeLabel', width: 'col', type: 'text' },
    { header: translate('compliance.library.columns.assignedAssets'), value: 'assignedAssets', width: 'col-2', type: 'text', centered: true },
    { header: translate('compliance.library.columns.action'), value: 'action', width: 'col-1', type: 'action' },
  ];
}

export interface AssignedAssets {
  id: number;
  name: string;
  type: string;
  makeModel?: string;
  weight?: string;
  frequencyId?: number;
  complianceFrequency?: string;
  /** Display label for `complianceFrequency` (a frequency or routine type name), which stays raw. */
  complianceFrequencyLabel?: string;
  isCompliant?: boolean | null;
  status?: string;
}

export interface ComplianceAssignedAsset {
  assetId: number;
}

export interface Checklist {
  id: number;
  name: string;
  allowsDocUpload: boolean;
  isArchived: boolean;
}

export interface DocumentChecklist {
  checklistType: string;
  checklist: Checklist[]
}

export interface InspectionChecklist {
  checklistType: string;
  checklist: Checklist[];
}

export interface ComplianceFrequency {
  id: number;
  name: string;
}

export interface RoutineType {
  id: number;
  name: string;
  /** Text in the user's language, resolved by the API; falls back to the raw name. */
  localizedName?: string | null;
}

export enum ComplianceTypeEnum {
  Periodic = 1,
  Routine = 2
}

export const COMPLIANCE_TYPES = [
  { id: ComplianceTypeEnum.Periodic, name: 'Periodic' },
  { id: ComplianceTypeEnum.Routine, name: 'Routine' }
] as const;

export const COMPLIANCE_TYPE_NAME_KEYS: Record<ComplianceTypeEnum, string> = {
  [ComplianceTypeEnum.Periodic]: 'compliance.library.complianceTypes.periodic',
  [ComplianceTypeEnum.Routine]: 'compliance.library.complianceTypes.routine'
};

export interface CustomComplianceRequirement {
  name: string;
  documentTypeId: number | null;
  documentTypeName?: string | null;
  critical?: boolean;
}

export interface CustomComplianceChecklist {
  name: string;
  checklistTypeId: number;
  requirements: CustomComplianceRequirement[];
}

export interface GetCompliancesRequest extends PaginatedSearchRequest {
  accountIds: number[];
  regionId?: number | null;
  countryId?: number | null;
  complianceId?: number | null;
}

export interface GetAssignedAssetsRequest extends PaginatedSearchRequest {
  complianceId: number;
  assetType?: AssetType | null;
}

export interface DeleteAssignedAssetRequest {
  assetId: number;
  complianceId: number;
}

export interface EditAssignedAssetFrequencyRequest {
  assetId: number;
  complianceId: number;
  frequencyId: number;
}

export interface AssignedAssetComplianceHistoryRequest {
  assetId: number;
  complianceId: number;
  page: number;
  pageSize: number;
}

export interface AssignedAssetComplianceHistory {
  taskId: number;
  date: string | Date;
  type: string;
  documentCount: number;
  failedChecklistCategories: string | null;
  inspectorName: string;
  isCompliant: boolean;
}

export interface ChecklistRequirementResultsRequest {
  taskId: number;
  complianceId: number;
  assetId: number;
}

export interface ChecklistRequirementResultAttachment {
  reference: string;
  filename: string;
  extension: string;
  link: string;
  uploadedAt: string;
}

export interface ChecklistRequirementResult {
  requirementId: number;
  requirementName: string;
  requirementResultId: number;
  notes: string | null;
  requirementResultStatus: ComplianceResult;
  submissionId: number | null;
  attachments: ChecklistRequirementResultAttachment[];
}

export type ChecklistRequirementPhase = 'pre' | 'post';

export interface ChecklistRequirementResults {
  checklistId: number;
  checklistName: string;
  checklistType: ChecklistTypeIdEnum | string;
  routineTypeId?: number;
  phase?: ChecklistRequirementPhase;
  requirementResults: ChecklistRequirementResult[];
}

/** Display row for the checklist-requirement-results table. */
export type ChecklistRequirementResultRow = ChecklistRequirementResult & {
  notesLabel: string;
  requirementResultStatusLabel: string;
  documentLabel: string;
  documentUrl: string | null;
  attachmentLabel: string;
  checklistId: number;
  attachmentReference: string | null;
};

export interface AssignComplianceAssetsRequest {
  complianceId: number;
  assetIds: number[];
}

export interface ComplianceArchiveRequest {
  complianceId: number;
  accountIds: number[];
}

export interface CreateCustomComplianceRequest {
  accountIds: number[];
  name: string;
  regionIds: number[];
  countryIds: number[];
  complianceTypeId: number;
  frequencyId?: number;
  routineTypeId?: number;
  checklists: CustomComplianceChecklist[];
}

export interface ComplianceExcelColumnSettings {
  complianceStandardNameColumn: number;
  regionsColumn: number;
  complianceTypeColumn: number;
  frequencyColumn: number;
  complianceCategoryColumn: number;
  checklistTypeColumn: number;
  requirementsColumn: number;
}

export interface ComplianceImport {
  complianceStandardName: string;
  regions: string;
  complianceType: string;
  frequency: string;
  complianceCategory: string;
  checklistType: string;
  requirements: string;
  createdBy: string;
  createdAt: Date;
}

export interface UploadComplianceExcelRequest {
  accountIds: number[];
  compliances: ComplianceImport[];
}

export interface ReviewComplianceCategory {
  name: string;
  checklistType: string;
  requirements: string[];
}

export type ReviewComplianceError = ReviewError;

export interface ReviewComplianceItem extends ReviewItemBase {
  name: string;
  regionNames: string[];
  complianceType: string;
  scheduleName: string;
  categories: ReviewComplianceCategory[];
}

export interface ChecklistRequirement {
  id: number;
  name: string;
  allowsDocUpload: boolean;
  isEnabled: boolean;
  isCritical: boolean;
}

export interface ComplianceChecklist {
  id: number;
  name: string;
  checklistTypeId: number;
  requirements: ChecklistRequirement[];
}

export interface RequirementEditor {
  name: string;
  documentTypeId: number | null;
  critical: boolean;
}

export interface EditAssignedAssetRequirementRow {
  id: number;
  name: string;
  enabled: boolean;
}

export interface GetChecklistsRequest {
  complianceId: number;
  assetId?: number;
  isArchived: boolean;
}

export interface UpdateChecklistRequirementDetailsRequest {
  requirementId: number;
  complianceId: number;
  name: string;
  documentTypeId: number | null;
  /** null leaves the flag unchanged — for callers that don't manage criticality. */
  isCritical: boolean | null;
}

export interface AddComplianceChecklistRequirementsRequest {
  complianceId: number;
  isNewCategory: boolean;
  checklistTypeId: number;
  existingChecklistId: number | null;
  newCategoryName: string | null;
  requirements: { name: string; documentTypeId: number | null; isCritical: boolean }[];
  allowDocumentUpload: boolean;
}